import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { sessionTimeoutService } from './lib/sessionTimeout';
import { AdminLogin } from './components/AdminLogin';
import { Users } from './pages/Users';
import { Analytics } from './pages/Analytics';
import { ExpertDashboard } from './pages/ExpertDashboard';
import { EscalationDetail } from './pages/EscalationDetail';
import { CustomerConfig } from './pages/CustomerConfig';
import './App.css';
import './styles/utilities.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});


interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
}

const PrivateRoute = ({ children, requiredRole }: PrivateRouteProps) => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');

  // Check if user is logged in and has required role
  if (token && userStr) {
    try {
      const user = JSON.parse(userStr);
      const allowedRoles = ['super_admin', 'admin', 'expert'];
      const hasAllowedRole = user.roles?.some((role:string) => allowedRoles.includes(role));

      if (!hasAllowedRole) {
        // Clear unauthorized user data
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        return <Navigate to="/" replace />;
      }

      // If a requiredRole is specified, check for it — accepts either a
      // single exact role (existing behavior, unchanged) or a list of
      // roles where any one of them is sufficient. Super admin always
      // passes, since Super Admin can see/access every feature.
      if (requiredRole) {
        const required = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        const hasRequiredRole = user.roles?.includes('super_admin') || required.some((role) => user.roles?.includes(role));
        if (!hasRequiredRole) {
          return <Navigate to="/" replace />;
        }
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return <Navigate to="/" replace />;
    }
  }

  // Initialize token refresh on mount if user is logged in
  useEffect(() => {
    if (token) {
      // Token expiration event listener
      const handleTokenExpired = () => {
        console.log('Token expired, redirecting to login...');
        sessionTimeoutService.stopSession();
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
      };

      // Session timeout event listener
      const handleSessionTimeout = () => {
        console.log('Session timeout, redirecting to login...');
        sessionTimeoutService.stopSession();
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
      };

      window.addEventListener('auth-token-expired', handleTokenExpired);
      window.addEventListener('session-timeout', handleSessionTimeout);

      return () => {
        window.removeEventListener('auth-token-expired', handleTokenExpired);
        window.removeEventListener('session-timeout', handleSessionTimeout);
      };
    }
  }, [token]);

  return token ? <>{children}</> : <Navigate to="/auth/login" />;
};


// Helper to redirect root based on user role
const RootRedirect = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.roles?.includes('expert') && !user.roles?.includes('admin') && !user.roles?.includes('super_admin')) {
        return <Navigate to="/expert-dashboard" replace />;
      }
      // Default: admin or super_admin
      return <Navigate to="/users" replace />;
    } catch {
      // If parsing fails, fallback to login
      return <Navigate to="/auth/login" replace />;
    }
  }
  return <Navigate to="/auth/login" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/auth/login" element={<AdminLogin />} />
          {/* Regular admin routes */}
          <Route
            path="/users"
            element={
              <PrivateRoute requiredRole="admin">
                <Users />
              </PrivateRoute>
            }
          />
          {/* Expert routes (EXPERT, SUPER_ADMIN — Admin does not get Expert Dashboard) */}
          <Route
            path="/expert-dashboard"
            element={
              <PrivateRoute requiredRole="expert">
                <ExpertDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/expert-dashboard/:escalationId"
            element={
              <PrivateRoute requiredRole="expert">
                <EscalationDetail />
              </PrivateRoute>
            }
          />
          {/* Super admin only routes */}
          <Route
            path="/analytics"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Analytics />
              </PrivateRoute>
            }
          />
          <Route
            path="/customer-config"
            element={
              <PrivateRoute requiredRole="super_admin">
                <CustomerConfig />
              </PrivateRoute>
            }
          />
          {/* Catch all route */}
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
